/**
 * Pure DOM predicates behind MathEditor's **box-level** text styling.
 *
 * Selecting a text box and pressing Bold styles everything in it — the user
 * does not have to select the characters first. Two decisions sit under that,
 * and both are easy to get subtly wrong, so they live here where jsdom can test
 * them directly rather than inside a 6900-line component that needs a real
 * browser to mount.
 *
 * Neither function reads `window`; everything they need is passed in.
 */
/**
 * Does `range` count as a real selection the user made *in this editor*?
 *
 * Every clause here is load-bearing, and three of them exist because of a
 * specific way the naive version fails:
 *
 * - **non-collapsed** — a bare caret is not a selection; treating it as one is
 *   what made the box-level feature necessary in the first place.
 * - **still connected** — `applyFormatting` mutates with `extractContents()`,
 *   which detaches the nodes a previously saved range points at. A detached
 *   range is not a selection of anything.
 * - **inside this editor** — a caret left behind in a *different* text box must
 *   not be read as this box's selection. Every MathEditor on the page sees the
 *   same toolbar event.
 * - **the editor holds focus** — the saved-selection path exists only to
 *   recover a selection the browser destroyed on toolbar mousedown (Edge does
 *   this despite `preventDefault`). The ref it reads from is written on every
 *   non-collapsed `onSelect` and is **never cleared**, so without this clause a
 *   selection from minutes ago comes back to life and gets styled instead of
 *   the box — in the commonest flow there is: select a word, style it, click
 *   elsewhere, press Bold.
 *
 * Pass `activeElement` as `null` to skip the focus clause (correct for the
 * *live* selection, which by definition is current).
 */
export function isUsableSelection(range, editor, activeElement, options) {
    if (options === void 0) { options = {}; }
    if (!range || !editor)
        return false;
    if (range.collapsed)
        return false;
    if (!range.startContainer.isConnected)
        return false;
    if (!editor.contains(range.commonAncestorContainer))
        return false;
    if (options.requireFocus && !(activeElement && editor.contains(activeElement)))
        return false;
    return true;
}
/**
 * Does every run of visible text in `editor` sit inside a `tagName` element?
 *
 * Used for the toolbar highlight when there is no selection: with no caret,
 * `document.queryCommandState` has nothing to report on, and a Bold button
 * showing "off" while a click would turn bold *off* is worse than no highlight.
 *
 * Two things it deliberately does:
 *
 * - **Skips zero-width text.** Blank-line caret spacers (`U+200B`) and the
 *   empty text nodes `splitText` leaves behind carry no formatting a user can
 *   see; counting them would report a fully-bold box as mixed.
 * - **Bounds `closest` to the editor.** `closest(tagName)` walks past the
 *   editor's own root, so a `<strong>` wrapping the whole widget would make
 *   every box read as bold.
 *
 * Returns `false` for a box with no visible text at all — the caller decides
 * what a text-free box means (MathEditor falls through to its math-fields), and
 * "vacuously true" would light the button up on an empty box.
 */
export function allVisibleTextHasAncestor(editor, tagName, zeroWidthChar) {
    var _a, _b, _c;
    if (!editor)
        return false;
    var doc = editor.ownerDocument;
    if (!doc)
        return false;
    var zws = new RegExp(zeroWidthChar, 'g');
    var walker = doc.createTreeWalker(editor, NodeFilter.SHOW_TEXT);
    var sawVisibleText = false;
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
        if (((_a = n.textContent) !== null && _a !== void 0 ? _a : '').replace(zws, '').length === 0)
            continue;
        sawVisibleText = true;
        var tag = (_c = (_b = n.parentElement) === null || _b === void 0 ? void 0 : _b.closest(tagName)) !== null && _c !== void 0 ? _c : null;
        if (!tag || !editor.contains(tag))
            return false;
    }
    return sawVisibleText;
}
/** True when `editor` holds no visible (non-zero-width) text at all. */
export function boxHasNoVisibleText(editor, zeroWidthChar) {
    var _a;
    if (!editor)
        return true;
    var zws = new RegExp(zeroWidthChar, 'g');
    return ((_a = editor.textContent) !== null && _a !== void 0 ? _a : '').replace(zws, '').trim().length === 0;
}
